/**
 * Collections Routes Configuration
 */

import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';

// Import routes from core modules
import { reportsRoutes } from '../../../../core/reports/routes';
import { FINANCE_BANNER, ModuleBannerProvider } from '../../../../core/layout/moduleBanner';
import GlobalUdaHeaderSelect from '@/components/GlobalUdaHeaderSelect';

// Shared Setup page
const SetupPage = lazy(() => import('../../../../core/setup'));
// Collections Dashboard — headline collected/outstanding across LRS, BPS, DPMS, Bhoo
// Konugolu, GPBP and this module's own counter collections, with a one-level drill into
// each. See masters/dashboard/index.tsx for why it exists and what it deliberately does
// not duplicate.
const CollectionsDashboard = lazy(() => import('../masters/dashboard'));

// Simple pass-through layout for nested routes
const PassThroughLayout = () => <Outlet />;

/*
 * Masters.
 *
 * Counter/Locations, Service Details and Collection Headers are reached from the Setup
 * Configuration page only — migration 011 took their sidebar rows (1001079-1001081) off,
 * because Setup already listed all three and two entrances to one screen read as two
 * screens. Their routes stay declared below: Setup's cards navigate to these slugs, and
 * the old sidebar URLs are still bookmarked.
 *
 * Migrations 013, 014 and 020 did the same for Approval Inbox (1001119), Bill Register
 * (1001127), Miscellaneous Receipt (1001116), Payment Gateway (1001129),
 * Approver / Remitter (1001117) and Revenue Head Mapping (1001124). Those routes below are
 * therefore reached from Setup, not the sidebar — do not read the absence of a menu row as
 * the screen being dead.
 *
 * What still has a sidebar row: Demand Register, Receipt Management, Search Receipts,
 * Defaulter Management, Refund Processing, Challan Management, Setup,
 * Reports — plus Bank Reconciliation, which is a deep link into Finance2 rather than a
 * screen in this module (see migration 013).
 *
 * Reconciliation (1001130) is gone from the sidebar: the collected-vs-banked screen
 * MOVED to Finance2, it did not lose its menu row while staying here. See below.
 */
const CounterLocation = lazy(() => import('../masters/counter-location'));
const ServiceDetails = lazy(() => import('../masters/service-details'));
const CollectionHeaders = lazy(() => import('../masters/collection-headers'));
const ApproverRemitter = lazy(() => import('../masters/approver-remitter'));
const BankServiceMapping = lazy(() => import('../masters/bank-service-mapping'));
const BranchUserMap = lazy(() => import('../masters/branch-user-map'));
const DemandRegister = lazy(() => import('../masters/demand-register'));
const BillGeneration = lazy(() => import('../masters/bill-generation'));
const ReceiptManagement = lazy(() => import('../masters/receipt-management'));
const DefaulterManagement = lazy(() => import('../masters/defaulter-management'));
const RefundProcessing = lazy(() => import('../masters/refund-processing'));
const RevenueHeadMapping = lazy(() => import('../masters/revenue-head-mapping'));

/*
 * Organisation masters, reached from the Collections Setup page. Registered as setup
 * cards by migration 025.
 *
 * THE SCREENS ARE REUSED, NOT COPIED. These render Employee Management's own master
 * components, so a department edited here is the same row edited there. Finance2 took
 * exactly this route for the same two masters (its migration 075, routes
 * /app/maud-erp/finance2/setup/department and /setup/designation) — a second editor
 * would give the organisation two department lists that drift, and no way to tell which
 * one a record came from.
 *
 * Why they belong on a collections setup page: a receipt is taken by a person, and who
 * that person is — their department and designation — is what the approver, remitter and
 * collector mappings on this same page are configured against. Until now the only place
 * to add one was Employee Management's setup, which is not where a collections officer
 * looks.
 */
const Department = lazy(() => import('../../employee-management/masters/departments'));
const Designation = lazy(() => import('../../employee-management/masters/designations'));
const EmployeeList = lazy(() => import('../../employee-management'));

/*
 * Challan Management is ONE page for the whole challan lifecycle. Its four old menu rows
 * (create, inbox, search, receipt) were a form button and three status filters over one
 * table, and splitting them made a clerk leave the screen to finish one challan. The tabs
 * are the status windows now.
 *
 * Search Receipts / Approval Inbox stay one component with a mode, because those two ARE
 * separate jobs, usually done by different people.
 */
const ChallanManagement = lazy(() => import('../masters/challan'));
const ReceiptSearchPage = lazy(() => import('../masters/receipt-search'));
const MiscellaneousReceipt = lazy(() => import('../masters/miscellaneous-receipt'));
/*
 * Reconciliation has MOVED to Finance2 (migration 013). The collected-vs-banked
 * question is now the "Collections → Bank" and "Trace" tabs on
 * /maud-erp/finance2/bank-reconciliation, beside the book-vs-statement and
 * book-vs-Tally reconciliations.
 *
 * Both paths below redirect there rather than 404ing, because this URL is
 * bookmarked and appears in the reports block. `replace` keeps the dead URL out
 * of history, so Back does not bounce the reader between the two.
 *
 * The component is intentionally NOT deleted and NOT imported: the screen and
 * its API still work, so 014 restores this in one line if the move is reverted.
 * Leaving the lazy() import in place while nothing rendered it would have made
 * the file look live to anyone reading for what to maintain.
 */
const RECONCILIATION_MOVED_TO = '/app/maud-erp/finance2/bank-reconciliation';
/*
 * Remittance — the same screen, mounted under its own name for the cashiers who do the
 * banking. The `reconciliation` slugs above keep redirecting to Finance2; this is a
 * separate sidebar row (migration 031) over the collections endpoints, which authorise on
 * dscl_collectionheader and so need no Finance2 menu or ledger access.
 */
const Remittance = lazy(() => import('../masters/reconciliation'));
const PaymentGateway = lazy(() => import('../masters/payment-gateway'));
/*
 * Daily Collection was removed (migration 033 retired its sidebar row 1001125 and Setup
 * card 1000612). Its old URLs land on the Collections dashboard instead of 404ing.
 */
const DAILY_COLLECTION_REMOVED_TO = '/app/maud-erp/collections/dashboard';

export const collectionsRoutes: RouteObject[] = [
  {
    // The module's landing page. Used to open straight on Setup, which answered
    // no question a visitor to "Collections" was actually asking — Setup keeps
    // its own row and its own /setup route below, unchanged.
    index: true,
    element: <CollectionsDashboard />
  },
  {
    path: 'dashboard',
    element: <CollectionsDashboard />
  },
  {
    path: 'reports',
    /* The shared Reports screen draws the module banner here (see moduleBanner). */
    element: <ModuleBannerProvider value={{ ...FINANCE_BANNER, title: 'Reports' }}><PassThroughLayout /></ModuleBannerProvider>,
    // Demand Register is a real page (auto-generated lease/recurring-fee demands), not a
    // DB report — declare it before the generic ':id' report route so it wins the match.
    children: [
      { path: 'demand-register', element: <DemandRegister /> },
      // Bill Generation (revenue bills derived from demands). Several likely slugs map to
      // the same page so whichever the 'Bill Generation'/'Bill Register' menu item uses,
      // it opens this page instead of the (empty/erroring) DB report. Declared before the
      // generic ':id' report route so these static slugs win the match.
      { path: 'bill-generation', element: <BillGeneration /> },
      { path: 'bill-register', element: <BillGeneration /> },
      { path: 'bills', element: <BillGeneration /> },
      { path: 'billing', element: <BillGeneration /> },
      // Receipt Management — multi-mode receipts (not a DB report)
      { path: 'receipt-management', element: <ReceiptManagement /> },
      { path: 'receipts', element: <ReceiptManagement /> },
      { path: 'search-receipts', element: <ReceiptManagement /> },
      // Defaulter Management is NOT a DB report — it is the arrears register, with
      // write actions on it. Its menu row (mnu_itm_id 1001128) is moved out of this
      // path by migrations/006_collections_defaulter_management_menu.js; these two
      // slugs stay declared here (before the generic ':id' report route) so the old
      // '/reports/defaulters' URL and any bookmark of it still open the real screen
      // instead of resolving 'defaulters' as a report id and finding nothing.
      { path: 'defaulters', element: <DefaulterManagement /> },
      { path: 'defaulter-management', element: <DefaulterManagement /> },
      // Refund Processing — same story: a workflow screen, not a report. Its menu row
      // (mnu_itm_id 1001131) is moved off this path by migration 008; both slugs stay
      // here so the old '/reports/refunds' URL keeps opening the real screen.
      { path: 'refunds', element: <RefundProcessing /> },
      { path: 'refund-processing', element: <RefundProcessing /> },
      /*
       * Payment Gateway is a working screen whose menu row still carries a /reports/
       * URL (mnu_itm_id 1001129). Declared here, before the
       * generic ':id' route, so the slug opens the real screen rather than being
       * resolved as a report id that does not exist.
       *
       * Reconciliation (1001130) kept the same treatment for the same reason, but now
       * redirects: the screen moved to Finance2 and this slug only has to survive as a
       * bookmark. Payment Gateway did NOT move — it answers whether an online payment
       * reached a receipt, which was never part of the collected-vs-banked question.
       */
      { path: 'reconciliation', element: <Navigate to={RECONCILIATION_MOVED_TO} replace /> },
      { path: 'online-payments', element: <PaymentGateway /> },
      { path: 'daily-collection', element: <Navigate to={DAILY_COLLECTION_REMOVED_TO} replace /> },
      ...reportsRoutes
    ]
  },
  {
    path: 'setup',
    element: <ModuleBannerProvider value={{ ...FINANCE_BANNER, title: 'Setup', udaSlot: <GlobalUdaHeaderSelect /> }}><SetupPage /></ModuleBannerProvider>
  },
  // Setup routes (mapped from setup options)
  {
    path: 'setup/counter-location',
    element: <CounterLocation />
  },
  // Masters routes
  {
    path: 'masters/counter-location',
    element: <CounterLocation />
  },
  // Direct routes (without prefix)
  {
    path: 'counter-location',
    element: <CounterLocation />
  },
  // Service Details routes
  {
    path: 'setup/service-details',
    element: <ServiceDetails />
  },
  {
    path: 'masters/service-details',
    element: <ServiceDetails />
  },
  {
    path: 'service-details',
    element: <ServiceDetails />
  },
  // Collection Headers routes
  {
    path: 'setup/collection-headers',
    element: <CollectionHeaders />
  },
  {
    path: 'masters/collection-headers',
    element: <CollectionHeaders />
  },
  {
    path: 'collection-headers',
    element: <CollectionHeaders />
  },
  // Approver -> Remitter Mapping routes
  {
    path: 'setup/approver-remitter',
    element: <ApproverRemitter />
  },
  {
    path: 'masters/approver-remitter',
    element: <ApproverRemitter />
  },
  {
    path: 'approver-remitter',
    element: <ApproverRemitter />
  },
  // Bank Service Mapping routes
  {
    path: 'setup/bank-service-mapping',
    element: <BankServiceMapping />
  },
  {
    path: 'masters/bank-service-mapping',
    element: <BankServiceMapping />
  },
  {
    path: 'bank-service-mapping',
    element: <BankServiceMapping />
  },
  // Bank Branch -> User Mapping routes
  {
    path: 'setup/branch-user-map',
    element: <BranchUserMap />
  },
  {
    path: 'masters/branch-user-map',
    element: <BranchUserMap />
  },
  {
    path: 'branch-user-map',
    element: <BranchUserMap />
  },
  // Demand Register + Bill Generation direct routes (in case the menu URL omits /reports)
  { path: 'demand-register', element: <DemandRegister /> },
  { path: 'bill-generation', element: <BillGeneration /> },
  { path: 'bill-register', element: <BillGeneration /> },
  { path: 'setup/bill-generation', element: <BillGeneration /> },
  { path: 'setup/bill-register', element: <BillGeneration /> },
  // Organisation masters — see the note by their imports. Employee Management's own
  // screens, mounted here so the Setup cards from migration 025 have somewhere to land.
  { path: 'setup/department', element: <Department /> },
  { path: 'setup/designation', element: <Designation /> },
  { path: 'setup/employees', element: <EmployeeList /> },
  { path: 'receipt-management', element: <ReceiptManagement /> },
  { path: 'receipts', element: <ReceiptManagement /> },
  { path: 'search-receipts', element: <ReceiptManagement /> },
  // Where the menus point once migrations 006 and 008 are applied.
  { path: 'defaulters', element: <DefaulterManagement /> },
  { path: 'defaulter-management', element: <DefaulterManagement /> },
  { path: 'refunds', element: <RefundProcessing /> },
  { path: 'refund-processing', element: <RefundProcessing /> },
  // Revenue Head Mapping — service -> GL account. Reached from the Setup page; its sidebar
  // row (1001124) was retired by migration 020 because Setup already carried the card. The
  // screen was never filed under the reports path, so both slugs stay flat.
  { path: 'revenue-head-mapping', element: <RevenueHeadMapping /> },
  { path: 'revenue-heads', element: <RevenueHeadMapping /> },

  /*
   * Challan Management — the menu registers these four under /receipt-services/.
   * One component, four modes.
   */
  /*
   * Challan Management — ONE page for the whole lifecycle. The menu now carries a single
   * row (migration 010); the four old URLs stay routed so bookmarks and any profile the
   * migration did not touch still land somewhere sensible, each on the tab that matches
   * what its old screen showed.
   */
  { path: 'challan-management', element: <ChallanManagement /> },
  { path: 'challans', element: <ChallanManagement /> },

  // Legacy entrances.
  { path: 'receipt-services/create-challan', element: <ChallanManagement autoOpen /> },
  { path: 'receipt-services/challan-inbox', element: <ChallanManagement initialTab="pending" /> },
  { path: 'receipt-services/search-challan', element: <ChallanManagement initialTab="all" /> },
  { path: 'receipt-services/challan-receipt', element: <ChallanManagement initialTab="collected" /> },
  { path: 'receipt-services/challan-management', element: <ChallanManagement /> },
  { path: 'create-challan', element: <ChallanManagement autoOpen /> },
  { path: 'challan-inbox', element: <ChallanManagement initialTab="pending" /> },
  { path: 'search-challan', element: <ChallanManagement initialTab="all" /> },
  { path: 'challan-receipt', element: <ChallanManagement initialTab="collected" /> },

  // Miscellaneous Receipt — adhoc counter collection.
  { path: 'receipt-services/miscellaneous-receipt', element: <MiscellaneousReceipt /> },
  { path: 'miscellaneous-receipt', element: <MiscellaneousReceipt /> },

  /*
   * Search Receipts and the Approval Inbox are the same register with a different status
   * window, so they share a component too.
   *
   * NOTE the ordering hazard: 'search-receipts' is ALSO declared under the reports block
   * above, where it maps to Receipt Management. The menu row for Search Receipts points at
   * /receipt-services/search-receipts, which is this one; the reports alias is left alone
   * so the older link keeps working.
   */
  { path: 'receipt-services/search-receipts', element: <ReceiptSearchPage mode="search" /> },
  { path: 'collection-workflow/approval-inbox', element: <ReceiptSearchPage mode="inbox" /> },
  { path: 'approval-inbox', element: <ReceiptSearchPage mode="inbox" /> },

  // Reconciliation and Payment Gateway. Their menu rows still sit under
  // /reports/ (reconciliation, online-payments) — see the reports block
  // above, which declares those slugs before the generic ':id' report route.
  { path: 'reconciliation', element: <Navigate to={RECONCILIATION_MOVED_TO} replace /> },
  { path: 'remittance', element: <Remittance /> },
  { path: 'remittances', element: <Remittance /> },
  { path: 'online-payments', element: <PaymentGateway /> },
  { path: 'payment-gateway', element: <PaymentGateway /> },
  { path: 'daily-collection', element: <Navigate to={DAILY_COLLECTION_REMOVED_TO} replace /> }
];

export default collectionsRoutes;
